import { aggregateUseCase } from "../../../../usecases/object";
import { _toISOString } from "../../helper/dateHelper";
import { buildSchedulePipeline } from "./SchedulePipeline";

export const fetchEmployeeSchedules = async (employeeId, maxDateStr, cache) => {
  if (!employeeId || !maxDateStr) return [];
  
  let employeeSchedules = cache.byEmployee.get(employeeId);
  
  if (!employeeSchedules) {
    try {
      const pipeline = buildSchedulePipeline(employeeId, maxDateStr);
      const results = await aggregateUseCase().execute("selected_schedule_assignment", pipeline);
      
      // Normalize created dates to "YYYY-MM-DD" format for efficient comparison
      employeeSchedules = results.map(schedule => ({
        ...schedule,
        createdDateStr: schedule.created ? _toISOString(new Date(schedule.created)) : ''
      }));

      cache.byEmployee.set(employeeId, employeeSchedules);
    } catch (error) {
      console.error(`Error fetching schedule for employee ${employeeId}:`, error);
      cache.byEmployee.set(employeeId, []);
      return [];
    }
  } else {
    // We have cached schedules, but check if we need to fetch newer ones
    // Find the latest cached schedule's created date
    const latestCachedDate = employeeSchedules.length > 0 
    // ? (employeeSchedules[0].created ? new Date(employeeSchedules[0].created).toISOString().split("T")[0] : null)
      ? (employeeSchedules[0].createdDateStr || null)
      : null;
    
    // If the requested maxDate is newer than our latest cached schedule, fetch updates
    if (latestCachedDate && maxDateStr > latestCachedDate) {
      try {
        const pipeline = buildSchedulePipeline(employeeId, maxDateStr, latestCachedDate);
        
        const results = await aggregateUseCase().execute(
          "selected_schedule_assignment",
          pipeline
        );
        
        // Normalize created dates to "YYYY-MM-DD" format for efficient comparison
        const newSchedules = results.map(schedule => ({
          ...schedule,
          createdDateStr: schedule.created ? _toISOString(new Date(schedule.created)) : ''
        }));
        employeeSchedules = [...newSchedules, ...employeeSchedules]
          .sort((a, b) => { return b.createdDateStr.localeCompare(a.createdDateStr); });
        cache.byEmployee.set(employeeId, employeeSchedules);
      } catch (error) {
        console.error(`Error fetching updated schedule for employee ${employeeId}:`, error);
      }
    }
  }
  
  return employeeSchedules;
};

export const fetchEmployeeSchedulesBatch = async (
  employeeIds,
  maxDateStr,
  cache,
  batchSize
) => {

  if (!cache?.byEmployee) cache.byEmployee = new Map();

  const toFetch = employeeIds.filter((id) => !cache.byEmployee.has(id));
  if (!toFetch.length) {
    return cache.byEmployee;
  }

  const createdDateStringExpr = {
    $cond: {
      if: { $eq: [{ $type: "$created" }, "date"] },
      then: {
        $dateToString: {
          date: "$created",
          format: "%Y-%m-%d",
        },
      },
      else: {
        $cond: {
          if: { $eq: [{ $type: "$created" }, "string"] },
          then: { $substr: ["$created", 0, 10] },
          else: { $toString: "$created" },
        },
      },
    },
  };

  const buildBatchPipeline = (batch) => ([
    {
      $match: {
        $expr: {
          $and: [
            { $in: ["$employeeId", batch] },
            { $lte: [createdDateStringExpr, maxDateStr] },
          ],
        },
      },
    },
    {
      $addFields: {
        createdDate: {
          $cond: {
            if: { $eq: [{ $type: "$created" }, "date"] },
            then: "$created",
            else: {
              $cond: {
                if: { $eq: [{ $type: "$created" }, "string"] },
                then: { $toDate: "$created" },
                else: null,
              },
            },
          },
        },
      },
    },
    { $sort: { employeeId: 1, createdDate: -1 } },
    {
      $project: {
        employeeId: 1,
        scheduleArray: 1,
        name: 1,
        selectedOption: 1,
        sequence: 1,
        startingDate: 1,
        created: 1,
      },
    },
  ]);

  for (let i = 0; i < toFetch.length; i += batchSize) {
    const batch = toFetch.slice(i, i + batchSize);
    try {
      // Initialize cache buckets for this batch.
      for (const empId of batch) {
        cache.byEmployee.set(empId, []);
      }

      const pipeline = buildBatchPipeline(batch);
      const results = await aggregateUseCase().execute(
        "selected_schedule_assignment",
        pipeline,
      );

      for (const doc of results || []) {
        const empId = doc?.employeeId;
        if (!empId) continue;
        if (!cache.byEmployee.has(empId)) continue;
        cache.byEmployee.get(empId).push({
          ...doc,
          createdDateStr: doc.created ? _toISOString(new Date(doc.created)) : "",
        });
      }
    } catch (error) {
      console.error(`Error fetching schedules batch:`, error);
      for (const empId of batch) {
        cache.byEmployee.set(empId, []);
      }
    }
  }

  return cache.byEmployee;
};
